<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-6xl mx-auto">
      
      <!-- HEADER -->
      <div class="sci-page-header pb-4 px-2">
        <div class="flex items-center gap-4">
          <button @click="$router.push('/supplies-manager')" class="flex items-center gap-2 text-slate-500 hover:text-orange-600 transition-colors mb-4 self-start">
            <v-icon small color="inherit">mdi-arrow-left</v-icon>
            <span class="text-[10px] font-black uppercase tracking-widest">Voltar</span>
          </button>
        </div>
        <h1 class="sci-page-header-title text-2xl font-black text-slate-800 uppercase tracking-tight">
          {{ id ? 'Editar Abastecimento' : 'Novo Abastecimento Administrativo' }}
        </h1>
        <p class="text-[11px] text-slate-400 font-bold uppercase tracking-wider mt-1">CADASTRO DE AUTORIZAÇÃO EXTRA (TIPO 01)</p>
      </div>

      <div class="space-y-8 pb-12">
        <!-- MAIN CARD -->
        <section class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xl shadow-slate-200/50">
          <!-- Header Escuro -->
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
            <v-icon color="orange" small>mdi-form-select</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
              DETALHES DA AUTORIZAÇÃO
            </h2>
          </div>

          <div class="p-8 bg-white">
            <v-form ref="form" v-model="valid" lazy-validation autocomplete="off">
              <div class="grid grid-cols-1 md:grid-cols-12 gap-8">
                
                <!-- Coluna Esquerda: Seleções -->
                <div class="md:col-span-7 space-y-6">
                  <!-- Entidades -->
                  <div class="bg-slate-50 p-6 rounded-2xl border border-slate-200 space-y-6 shadow-inner transition-all focus-within:border-orange-500 focus-within:ring-1 focus-within:ring-orange-500/10">
                    <h3 class="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-200 pb-2 mb-4">ENTIDADES E VÍNCULOS</h3>
                    
                    <div class="space-y-4">
                      <div v-if="$store.getters.getUserData.group_id == 1 || $store.getters.getUserData.group_id == 5 || $store.getters.getUserData.table_key != 'prefectures'">
                        <label class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Município (Prefeitura)</label>
                        <v-autocomplete
                          v-model="data.prefecture_id"
                          :items="prefectures"
                          item-value="prefecture_id"
                          item-text="prefecture_social_name"
                          placeholder="SELECIONE A PREFEITURA"
                          @change="[getDrivers(data.prefecture_id), getProviders(data.prefecture_id)]"
                          outlined dense hide-details
                          class="bg-white rounded-lg shadow-sm font-bold uppercase"
                          prepend-inner-icon="mdi-city-variant-outline"
                          :rules="fieldRules"
                        />
                      </div>
                      
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Fornecedor</label>
                          <v-autocomplete
                            v-model="data.provider_id"
                            :items="providers"
                            item-value="provider_id"
                            item-text="provider_name"
                            placeholder="SELECIONE O FORNECEDOR"
                            @change="loadItems(data.provider_id)"
                            outlined dense hide-details
                            class="bg-white rounded-lg shadow-sm font-bold uppercase"
                            prepend-inner-icon="mdi-store-outline"
                            :rules="fieldRules"
                          />
                        </div>

                        <div>
                          <label class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Item / Combustível</label>
                          <v-autocomplete
                            v-model="data.item_id"
                            :items="items"
                            item-value="item_id"
                            item-text="item_name"
                            placeholder="SELECIONE O ITEM"
                            outlined dense hide-details
                            class="bg-white rounded-lg shadow-sm font-bold uppercase"
                            prepend-inner-icon="mdi-gas-station-outline"
                            :rules="fieldRules"
                            no-data-text="Nenhum item encontrado"
                          >
                            <template slot="selection" slot-scope="{ item }">
                              <span class="text-xs font-black">{{ item.item_description_desc }}</span>
                              <span class="text-[10px] text-emerald-600 font-bold ml-2">R$ {{ realMask(item.item_price.toFixed(2)) }}</span>
                            </template>
                            <template slot="item" slot-scope="{ item }">
                              <div class="flex items-center justify-between w-full py-2">
                                <span class="text-xs font-black uppercase">{{ item.item_description_desc }}</span>
                                <span class="text-[10px] font-black text-emerald-600">R$ {{ realMask(item.item_price.toFixed(2)) }}</span>
                              </div>
                            </template>
                        </v-autocomplete>
                        </div>
                      </div>

                      <div>
                        <label class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Motorista Autorizado</label>
                        <v-autocomplete
                          v-model="data.driver_id"
                          :items="drivers"
                          item-value="driver_id"
                          item-text="driver_name"
                          placeholder="SELECIONE O MOTORISTA"
                          outlined dense hide-details
                          class="bg-white rounded-lg shadow-sm font-bold uppercase"
                          prepend-inner-icon="mdi-account-outline"
                          append-icon="mdi-plus-circle-outline"
                          @click:append="showForm = true"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Coluna Direita: Valores e Observações -->
                <div class="md:col-span-5 space-y-6">
                  <div class="bg-slate-50 p-6 rounded-2xl border border-slate-200 space-y-6 shadow-inner">
                    <h3 class="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-200 pb-2 mb-4">LIMITES E JUSTIFICATIVA</h3>
                    
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div class="bg-white p-4 rounded-xl border border-slate-200 shadow-sm transition-all focus-within:border-orange-500">
                        <label class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Litros Autorizados</label>
                        <v-text-field
                          v-model="data.supply_authorized_liters"
                          v-mask-decimal.br="3"
                          placeholder="0,000"
                          outlined dense hide-details
                          class="bg-white rounded-lg font-black text-center text-emerald-600"
                          :disabled="disableLitros"
                        />
                      </div>
                      <div class="bg-white p-4 rounded-xl border border-slate-200 shadow-sm transition-all focus-within:border-indigo-500">
                        <label class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Valor Total (R$)</label>
                        <v-text-field
                          v-model="data.supply_authorized_value"
                          v-mask-decimal.br="2"
                          placeholder="0,00"
                          outlined dense hide-details
                          class="bg-white rounded-lg font-black text-center text-indigo-700"
                          prefix="R$"
                          :disabled="disableValor"
                        />
                      </div>
                    </div>

                    <div class="bg-amber-50/50 p-4 rounded-xl border border-amber-100 shadow-sm">
                      <label class="text-[10px] font-black text-amber-500 uppercase tracking-widest mb-2 block">Justificativa / Motivo</label>
                      <v-textarea
                        v-model="data.justificativa"
                        placeholder="DESCREVA O MOTIVO DESTA AUTORIZAÇÃO..."
                        outlined dense hide-details
                        class="bg-white rounded-lg font-medium uppercase text-xs"
                        rows="4" auto-grow
                        :rules="fieldRules"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Footer Actions -->
              <div class="flex items-center justify-end gap-4 mt-12 pt-8 border-t border-slate-100">
                <button 
                  type="button"
                  @click="$router.push('/supplies-manager')"
                  class="text-slate-500 hover:text-slate-700 font-black text-[11px] uppercase tracking-widest px-6 py-2 transition-colors"
                >
                  CANCELAR
                </button>
                <button 
                  type="button"
                  @click="validate"
                  :disabled="!valid || loader"
                  class="bg-orange-500 hover:bg-orange-600 text-white font-black text-[11px] px-10 h-12 rounded-xl shadow-lg shadow-orange-500/20 transform hover:-translate-y-0.5 transition-all flex items-center gap-2 uppercase tracking-widest"
                >
                  <v-icon v-if="loader" small class="mdi-spin" color="white">mdi-loading</v-icon>
                  <v-icon v-else small color="white">mdi-check-bold</v-icon>
                  {{ id ? 'SALVAR ALTERAÇÕES' : 'CONFIRMAR AUTORIZAÇÃO' }}
                </button>
              </div>
            </v-form>
          </div>
        </section>
      </div>
    </div>

    <!-- MODAL NOVO MOTORISTA -->
    <v-dialog v-model="showForm" width="500">
      <v-card class="rounded-2xl overflow-hidden shadow-2xl border border-slate-200">
        <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
          <div class="flex items-center gap-3">
            <v-icon color="orange" small>mdi-account-plus-outline</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
              NOVO MOTORISTA
            </h2>
          </div>
          <v-btn icon dark small @click="showForm = false; driver_name = ''">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="p-8 bg-white">
          <v-form @submit.prevent="newDriver" class="space-y-6">
            <div class="bg-slate-50 p-4 rounded-xl border border-slate-200">
              <label class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Nome do Motorista</label>
              <v-text-field
                v-model="driver_name"
                placeholder="DIGITE O NOME COMPLETO..."
                outlined dense hide-details
                class="bg-white rounded-lg shadow-sm font-bold uppercase"
                prepend-inner-icon="mdi-account-outline"
                :rules="fieldRules"
              />
            </div>
            
            <div class="flex items-center justify-end gap-4 pt-6 border-t border-slate-100">
              <button 
                type="button"
                @click="showForm = false; driver_name = ''"
                class="text-slate-500 hover:text-slate-700 font-black text-[11px] uppercase tracking-widest px-4 py-2 transition-colors"
              >
                CANCELAR
              </button>
              <button 
                type="button"
                @click="newDriver"
                :disabled="!driver_name || loader"
                class="bg-orange-500 hover:bg-orange-600 text-white font-black text-[11px] px-8 h-12 rounded-xl shadow-lg shadow-orange-500/20 transform hover:-translate-y-0.5 transition-all flex items-center gap-2 uppercase tracking-widest"
              >
                <v-icon v-if="loader" small class="mdi-spin" color="white">mdi-loading</v-icon>
                <v-icon v-else small color="white">mdi-check-bold</v-icon>
                CONFIRMAR
              </button>
            </div>
          </v-form>
        </div>
      </v-card>
    </v-dialog>
    
    <!-- MODAL SENHA -->
    <v-dialog v-model="showPassword" width="400" persistent transition="dialog-bottom-transition">
      <v-card class="rounded-2xl overflow-hidden shadow-2xl border border-slate-200">
        <div class="p-8 text-center bg-slate-50 border-b border-slate-100">
          <div class="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
            <v-icon size="32" color="inherit">mdi-shield-check-outline</v-icon>
          </div>
          <h3 class="text-slate-400 font-black text-[10px] uppercase tracking-[0.2em] mb-2">Autorização Gerada</h3>
          <div class="relative group">
            <input
              readonly
              class="text-4xl font-black text-center w-full bg-white border-2 border-slate-200 rounded-2xl py-6 px-4 text-slate-800 shadow-xl shadow-slate-200/50 outline-none group-hover:border-emerald-400 transition-all font-mono"
              ref="copiar"
              :value="password"
            />
          </div>
          
          <p class="text-slate-400 text-[10px] font-bold uppercase mt-6 tracking-widest leading-relaxed">
            Utilize este código para autorizar o abastecimento.
          </p>
        </div>

        <div class="p-6 bg-white border-t border-slate-100">
          <button
            @click="copy"
            class="w-full h-14 rounded-xl font-black text-[11px] uppercase tracking-[0.15em] transition-all flex items-center justify-center gap-3 shadow-lg transform hover:-translate-y-1 active:translate-y-0"
            :class="copiado ? 'bg-emerald-500 text-white shadow-emerald-500/20' : 'bg-slate-900 text-white shadow-slate-900/20 hover:bg-slate-800'"
          >
            <v-icon size="18" color="white">{{ copiado ? 'mdi-check-all' : 'mdi-content-copy' }}</v-icon>
            {{ copiado ? 'CÓDIGO COPIADO!' : 'COPIAR CÓDIGO' }}
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- NOTIFICAÇÕES -->
    <v-snackbar
      v-model="snack.show"
      :color="snack.color"
      timeout="4000"
      rounded="xl"
      elevation="24"
      class="mb-8"
    >
      <div class="flex items-center gap-3">
        <v-icon color="white">{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        <span class="text-xs font-black uppercase tracking-widest">{{ snack.message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn dark icon v-bind="attrs" @click="snack.show = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Helpers from "@/mixins/helpers.js";
export default {
  props: ["id"],
  mixins: [Helpers],
  data: () => ({
    valid: false,
    loader: false,
    showForm: false,
    showPassword: false,
    copiado: false,
    password: "",
    providers: [],
    drivers: [],
    vehicles: [],
    items: [],
    prefectures: [],
    driver_name: "",
    fieldRules: [(v) => !!v || "Este campo é requerido"],
    snack: {
      show: false,
      message: "",
      color: "success",
    },
    data: {
      supply_id: null,
      supply_authorized_liters: "0",
      supply_authorized_value: "0",
      supply_status: "PRE-AUTORIZADO",
      supply_adjusted: false,
      supply_extra: true,
      supply_liters_stocked: false,
      supply_billed: false,
      vehicle_id: null,
      driver_id: null,
      item_id: null,
      prefecture_id: null,
      provider_id: null,
      justificativa: null,
    },
  }),
  created() {
    this.Helpers;
    const userData = this.$store.getters.getUserData;

    // Se for administrador ou não tiver vínculo específico, carregamos as prefeituras
    if (userData.group_id == 1 || userData.group_id == 5 || userData.table_key != 'prefectures') {
      this.$http.get("/prefectures").then((r) => (this.prefectures = r.data));
    }

    // Se tiver um vínculo específico de prefeitura, preenchemos e carregamos dependentes
    if (userData.table_key == "prefectures" && userData.table_id) {
       this.data.prefecture_id = userData.table_id;
       this.getProviders(this.data.prefecture_id);
       this.getDrivers(this.data.prefecture_id);
    }

    if (this.id) {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`supplies/single/${this.id}`).then((r) => {
        this.data = r.data[0];
        this.$http
          .get(`/supplies/providers/${r.data[0].provider_id}`)
          .then((i) => {
            this.items = i.data;
            this.dialog = true;
          });
        delete this.data.datehour;
        delete this.data.supply_liters_stocked;
        delete this.data.supply_price_day;
        this.$store.dispatch("setLoader", false);
      });
    }
  },
  computed: {
    disableLitros() {
      const val = (this.data.supply_authorized_value || "0").toString().replace(',', '.');
      return parseFloat(val) > 0;
    },
    disableValor() {
      const val = (this.data.supply_authorized_liters || "0").toString().replace(',', '.');
      return parseFloat(val) > 0;
    }
  },
  methods: {
    copy() {
      var copyTextarea = this.$refs.copiar;
      copyTextarea.select();
      try {
        var successful = document.execCommand("copy");
        if (successful) {
          this.copiado = true;
          setTimeout(() => {
            this.showPassword = false;
            this.copiado = false;
            this.$router.push("/supplies-manager");
          }, 1000);
        } else {
          alert("não copiado!");
        }
      } catch (err) {
        alert(
          "Opa, Não conseguimos copiar o texto, é possivel que o seu navegador não tenha suporte, tente usar Crtl+C."
        );
      }
    },
    loadItems(provider_id) {
      if (!provider_id || !this.data.prefecture_id) return;
      this.$http
        .get(`/supplies/providers/prefectures/${provider_id}/${this.data.prefecture_id}`)
        .then((r) => (this.items = r.data));
    },
    getProviders(prefecture_id) {
      if (!prefecture_id) return;
      this.$http
        .get(`/providers/prefecture/${prefecture_id}`)
        .then((r) => (this.providers = r.data));
    },
    getDrivers(prefecture_id) {
      if (!prefecture_id) return;
      this.$http
        .get(`/supplies/drivers/${prefecture_id}`)
        .then((r) => (this.drivers = r.data));
    },
    newDriver() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .post("/supplies/drivers", {
          driver_name: this.driver_name,
          prefecture_id: this.data.prefecture_id,
        })
        .then((r) => {
          this.snack.show = true;
          this.snack.message = r.data.result;
          this.snack.color = "success";
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
          this.$http.get("/drivers").then((d) => {
            this.drivers = d.data;
            const driverItems = d.data.filter((f) =>
              f.driver_name === this.driver_name ? f : null
            );
            console.log(driverItems[0].driver_id);
            this.data.driver_id = driverItems[0].driver_id;
            this.showForm = false;
          });
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.color = "error";
          this.snack.message = e.message;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    validate() {
      this.snack.show = true;
      this.loader = true;
      if (this.$refs.form.validate()) {
        this.createOrUpdate();
      } else {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message =
          "Atencão um ou mais campos precisam ser preenchidos!";
        this.loader = false;
      }
    },
    checkFields() {
      if (this.data.supply_authorized_value) {
        this.data.supply_authorized_liters = 0;
      } else {
        this.data.supply_authorized_value = 0;
      }
    },
    createOrUpdate() {
      this.loader = true;
      // this.checkFields();
      if (this.id) {
        this.$store.dispatch("setLoader", this.loader);
        this.$http
          .put(`/supplies/single/${this.id}`, this.data)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.loader = true;
            setTimeout(() => {
              this.$router.push("/supplies-manager");
            }, 1500);
            this.$store.dispatch("setLoader", this.loader);
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      } else {
        this.$store.dispatch("setLoader", this.loader);
        this.data.is_autorized = true;
        this.data.authorizer = this.$store.getters.getUserData.user_login;
        const litersStr = (this.data.supply_authorized_liters || "0").toString();
        const valueStr = (this.data.supply_authorized_value || "0").toString();
        const liters = parseFloat(litersStr.replace(',', '.'));
        const value = parseFloat(valueStr.replace(',', '.'));
        const price = parseFloat(this.data.supply_price_day) || parseFloat(this.data.supply_bidding_price) || 0;

        if (value === 0 && liters > 0) {
          this.data.supply_value = liters * price;
          this.data.supply_liters_stocked = liters;
        } else if (value > 0 && liters === 0) {
          this.data.supply_value = value;
          this.data.supply_liters_stocked = price > 0 ? value / price : 0;
          this.data.supply_authorized_liters = this.data.supply_liters_stocked.toFixed(3).replace('.', ',');
        } else {
          this.data.supply_value = value;
          this.data.supply_liters_stocked = liters;
        }

        this.$http
          .post("/supplies/single", this.data)
          .then((r) => {
            this.showPassword = true;
            this.$http
              .get(`/supplies/single/${r.data.idInserted}`)
              .then((r) => (this.password = r.data[0].supply_code));
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.loader = false;
            this.$store.dispatch("setLoader", this.loader);
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.response && e.response.data 
              ? (e.response.data.error || e.response.data.message || (typeof e.response.data === 'string' ? e.response.data : e.message)) 
              : e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      }
    },
  },
};
</script>

<style>
.title {
  text-transform: uppercase;
  font-weight: 400;
}

.flexd {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
}
.flexd p {
  margin: 10px 0;
  font-weight: bold;
  text-transform: uppercase;
}
</style>
